import { Box, CardMedia, useTheme } from "@mui/material";
import futureRobotImage from "../assets/futures-grid-bot-img.png";
import spotRobotImage from "../assets/spot-img.png";
import strategyRobotImage from "../assets/strategy-img.png";
import { ReactComponent as PlusIcon } from "../assets/plus-icon.svg";
import { FC } from "react";
import Text from "../../../_components/Text";
import { myT } from "../../../_i18n/utils";
import { useTranslation } from "react-i18next";

type BotType = "futures" | "auto-strategies" | "spot-bots";

const BOT_IMAGES: Record<BotType, string> = {
  futures: futureRobotImage,
  "spot-bots": spotRobotImage,
  "auto-strategies": strategyRobotImage,
};

const getBotEmptyText = (type: BotType): string => {
  switch (type) {
    case "futures":
      return myT("createFirstBot", "bots");
    case "auto-strategies":
      return myT("createFirstAutoStrategy", "bots");
    case "spot-bots":
      return myT("createFirstBot", "bots");
    default:
      return "";
  }
};

interface BotStatProps {
  label: string;
  value: number;
}

const BotStat: FC<BotStatProps> = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between">
    <Text variant="s">{label}</Text>
    <Text variant="s-semi">{value}</Text>
  </Box>
);

interface BotCardProps {
  type: BotType;
  title: string;
  activeCount?: number;
  archivedCount?: number;
  isSoon?: boolean;
  onClickCard?: () => void;
  disabled?: boolean;
}

const BotCard: FC<BotCardProps> = ({
  type,
  activeCount = 0,
  archivedCount = 0,
  title,
  isSoon,
  onClickCard,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("bots");

  const totalCount = activeCount + archivedCount;

  return (
    <Box sx={{ position: "relative" }} onClick={disabled ? undefined : onClickCard}>
      {isSoon && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translateX(-50%) translateY(-50%)",
            backgroundColor: theme.myTheme.colors.secondary,
            borderRadius: "12px",
            padding: "2px 10px",
            zIndex: 10,
          }}
        >
          <Text variant="s-semi" sx={{ opacity: "1" }}>
            {t("soon")}
          </Text>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "background.default",
          borderRadius: "16px",
          border: `1px solid ${theme.myTheme.colors.gray100}`,
          height: "95px",
          padding: "0 16px",
          opacity: isSoon ? 0.5 : 1,
          zIndex: 1,
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: "90px",
            height: "93px",
          }}
          src={BOT_IMAGES[type]}
        />

        <Box sx={{ flex: "1 1 auto", padding: "16px 0" }}>
          <Text variant="h3" mb={1}>
            {title.toUpperCase()}
          </Text>

          {totalCount ? (
            <>
              <BotStat label={t("active")} value={activeCount} />
              <BotStat label={t("archived")} value={archivedCount} />
            </>
          ) : (
            <Text variant="s">{getBotEmptyText(type)}</Text>
          )}
        </Box>

        {!totalCount && <PlusIcon color={theme.myTheme.colors.foreground50} />}
      </Box>
    </Box>
  );
};

export default BotCard;
